import { useMemo, useReducer } from 'react';

import { enableAllPlugins, original, produce } from 'immer';
import _ from 'lodash';
import moment from 'moment';
import voca from 'voca';

import { dashboardConfigurableFieldKey } from 'components/projectsDashboard/helpers';
import * as projectFiltersSchema from 'components/projectsDashboard/schemas/projectFiltersSchema';

enableAllPlugins();

export default function useProjectFilters({
  configurableFieldOptionFilterSelections,
  dashboardConfigurableFields,
  dateFilterSelections,
  folderFilterSelections,
  optionFilterSelections,
  userFilterSelections,
}) {
  const defaultFiltersArgs = { dashboardConfigurableFields };

  const defaultFilters = useMemo(
    () => buildProjectFilters(defaultFiltersArgs),
    [JSON.stringify(defaultFiltersArgs)],
  );

  const seededFiltersArgs = {
    configurableFieldOptionFilterSelections,
    dashboardConfigurableFields,
    dateFilterSelections,
    folderFilterSelections,
    optionFilterSelections,
    userFilterSelections,
  };

  const seededFilters = useMemo(
    () => buildProjectFilters(seededFiltersArgs),
    [JSON.stringify(seededFiltersArgs)],
  );

  const [projectFilters, dispatch] = useReducer(reducer, seededFilters);

  function handleProjectFilterAdd(filterKey) {
    dispatch({
      type: 'ADD_FILTER',
      payload: { filterKey },
    });
  }

  function handleProjectFilterRemove(filterKey) {
    dispatch({
      type: 'REMOVE_FILTER',
      payload: { filterKey, defaultFilters },
    });
  }

  function handleProjectFilterValueAdd({ filterKey, key, value }) {
    dispatch({
      type: 'ADD_FILTER_VALUE',
      payload: { filterKey, key, value },
    });
  }

  function handleProjectFilterValueRemove({ filterKey, key, value }) {
    dispatch({
      type: 'REMOVE_FILTER_VALUE',
      payload: { filterKey, key, value },
    });
  }

  function handleProjectFilterValueChange({ filterKey, key, value }) {
    dispatch({
      type: 'CHANGE_FILTER_VALUE',
      payload: { filterKey, key, value },
    });
  }

  function handleProjectFiltersCleanup() {
    dispatch({ type: 'CLEANUP', payload: { defaultFilters } });
  }

  function handleProjectFiltersReset() {
    dispatch({ type: 'RESET', payload: { defaultFilters } });
  }

  return {
    handleProjectFilterAdd,
    handleProjectFilterRemove,
    handleProjectFilterValueAdd,
    handleProjectFilterValueRemove,
    handleProjectFilterValueChange,
    handleProjectFiltersCleanup,
    handleProjectFiltersReset,
    projectFilters,
  };
}

function reducer(state, action) {
  return produce(state, (draftState) => {
    switch (action.type) {
      case 'ADD_FILTER': {
        const { filterKey } = action.payload;
        if (!draftState.selectedFilters.includes(filterKey)) {
          draftState.selectedFilters.push(filterKey);
        }
        break;
      }
      case 'REMOVE_FILTER': {
        const { defaultFilters, filterKey } = action.payload;
        resetFilter({ filterKey, draftState, defaultFilters });
        break;
      }
      case 'ADD_FILTER_VALUE': {
        const { filterKey, key, value } = action.payload;
        draftState[filterKey][key].push(value);
        draftState.validationErrors[filterKey] = validateFilter({
          data: draftState[filterKey],
          filterKey,
        });
        break;
      }
      case 'REMOVE_FILTER_VALUE': {
        const { filterKey, key, value } = action.payload;
        const valueIndex = draftState[filterKey][key].findIndex(
          (id) => id === value,
        );
        if (valueIndex !== -1) draftState[filterKey][key].splice(valueIndex, 1);
        draftState.validationErrors[filterKey] = validateFilter({
          data: draftState[filterKey],
          filterKey,
        });
        break;
      }
      case 'CHANGE_FILTER_VALUE': {
        const { filterKey, key, value } = action.payload;
        draftState[filterKey][key] = value;
        draftState.validationErrors[filterKey] = validateFilter({
          data: draftState[filterKey],
          filterKey,
        });
        break;
      }
      case 'CLEANUP': {
        const { defaultFilters } = action.payload;
        original(draftState.selectedFilters).forEach((selectedFilter) => {
          if (multiSelectIdsFilterKeys.includes(selectedFilter)) {
            if (
              draftState[selectedFilter].selectedIds.length === 0 ||
              Object.keys(draftState.validationErrors[selectedFilter]).length >
                0
            ) {
              resetFilter({
                defaultFilters,
                draftState,
                filterKey: selectedFilter,
              });
            }
          } else if (dateFilterKeys.includes(selectedFilter)) {
            if (
              Object.keys(draftState.validationErrors[selectedFilter]).length >
              0
            ) {
              resetFilter({
                defaultFilters,
                draftState,
                filterKey: selectedFilter,
              });
            }
          } else if (checkboxFilterKeys.includes(selectedFilter)) {
            if (
              !Object.values(draftState[selectedFilter]).some(Boolean) ||
              Object.keys(draftState.validationErrors[selectedFilter]).length >
                0
            ) {
              resetFilter({
                defaultFilters,
                draftState,
                filterKey: selectedFilter,
              });
            }
          } else if (selectedFilter.startsWith('dashboardConfigurableField')) {
            if (
              draftState[selectedFilter].selectedIds.length === 0 ||
              Object.keys(draftState.validationErrors[selectedFilter]).length >
                0
            ) {
              resetFilter({
                defaultFilters,
                draftState,
                filterKey: selectedFilter,
              });
            }
          }
        });
        break;
      }
      case 'RESET': {
        const { defaultFilters } = action.payload;
        return { ...defaultFilters };
      }
    }
  });
}

export function resetFilter({ defaultFilters, draftState, filterKey }) {
  const filterIndex = draftState.selectedFilters.findIndex(
    (id) => id === filterKey,
  );
  if (filterIndex !== -1) {
    draftState.selectedFilters.splice(filterIndex, 1);
  }
  draftState[filterKey] = defaultFilters[filterKey];
  draftState.validationErrors[filterKey] =
    defaultFilters.validationErrors[filterKey];
}

export function validateFilter({ data, filterKey }) {
  const filterSchemaKey =
    filterKey.startsWith('dashboardConfigurableField') ?
      'defaultMultiSelectIdsFilterSchema'
    : `${filterKey}Schema`;
  const safeParsedSchema =
    projectFiltersSchema[filterSchemaKey].safeParse(data);

  if (safeParsedSchema.error) {
    return safeParsedSchema.error.issues.reduce((acc, issue) => {
      acc[issue.path[0]] = {
        error: { detail: issue.message },
        fieldHighlighted: true,
      };
      return acc;
    }, {});
  } else {
    return {};
  }
}

const defaultMultiSelectIdsFilter = {
  selectedIds: [],
};

const defaultDateFilter = {
  withinTheNextFrequency: '2',
  withinTheNextInterval: 'weeks',
  withinTheLastFrequency: '2',
  withinTheLastInterval: 'weeks',
  before: null,
  after: null,
  onExactDate: null,
  customDateRangeFrom: null,
  customDateRangeTo: null,
};

const multiSelectIdsFilters = {
  folderFilter: { ...defaultMultiSelectIdsFilter },
  creatorFilter: { ...defaultMultiSelectIdsFilter },
  reviewerFilter: { ...defaultMultiSelectIdsFilter },
  approverFilter: { ...defaultMultiSelectIdsFilter },
};

const dateFilters = {
  reviewDateFilter: { ...defaultDateFilter, period: 'withinTheNext' },
  lastUpdatedFilter: { ...defaultDateFilter, period: 'withinTheLast' },
  creationDateFilter: { ...defaultDateFilter, period: 'withinTheLast' },
  startDateFilter: { ...defaultDateFilter, period: 'withinTheNext' },
};

const checkboxFilters = {
  lifecycleStatusFilter: {
    live: false,
    future: false,
    ended: false,
    noDates: false,
  },
  reviewStatusFilter: {
    pastDueDate: false,
    upcoming: false,
    upToDate: false,
    notSet: false,
  },
  approvalStatusFilter: {
    rejected: false,
    pendingApproval: false,
    approved: false,
    notSubmitted: false,
  },
  signatureStatusFilter: {
    hasRejections: false,
    pendingSignatures: false,
    fullySigned: false,
    notRequested: false,
  },
};

export const multiSelectIdsFilterKeys = Object.keys(multiSelectIdsFilters);
export const dateFilterKeys = Object.keys(dateFilters);
export const checkboxFilterKeys = Object.keys(checkboxFilters);

export function buildProjectFilters({
  configurableFieldOptionFilterSelections = [],
  dashboardConfigurableFields = [],
  dateFilterSelections = [],
  folderFilterSelections = [],
  optionFilterSelections = [],
  userFilterSelections = [],
}) {
  const seededFilterSelections = new Map();

  return produce({}, (draftState) => {
    (function setupInitialFilters() {
      _.merge(draftState, multiSelectIdsFilters, dateFilters, checkboxFilters);

      dashboardConfigurableFields.forEach((dashboardConfigurableField) => {
        const filterKey = dashboardConfigurableFieldKey({
          dashboardConfigurableField,
          suffix: 'Filter',
        });

        draftState[filterKey] = _.cloneDeep(defaultMultiSelectIdsFilter);
      });

      const filterKeys = Object.keys(draftState);

      draftState.validationErrors = {};
      filterKeys.forEach((filterKey) => {
        draftState.validationErrors[filterKey] = {};
      });
    })();

    (function seedConfigurableFieldOptionFilterSelections() {
      configurableFieldOptionFilterSelections.forEach((selection) => {
        const filterKey = dashboardConfigurableFieldKey({
          dashboardConfigurableField: selection.relationships.field.data,
          suffix: 'Filter',
        });

        setSeededFilterSelection({
          filterKey,
          createdAt: selection.attributes.createdAt,
        });

        draftState[filterKey].selectedIds.push(
          selection.attributes.value || null,
        );
      });
    })();

    (function seedOptionFilterSelections() {
      optionFilterSelections.forEach((selection) => {
        const filterKey = `${voca.camelCase(selection.attributes.filterType)}Filter`;

        setSeededFilterSelection({
          filterKey,
          createdAt: selection.attributes.createdAt,
        });

        draftState[filterKey][voca.camelCase(selection.attributes.value)] =
          true;
      });
    })();

    (function seedDateFilterSelections() {
      dateFilterSelections.forEach((selection) => {
        const filterKey = `${voca.camelCase(selection.attributes.filterType)}Filter`;
        const period = voca.camelCase(selection.attributes.period);

        setSeededFilterSelection({
          filterKey,
          createdAt: selection.attributes.createdAt,
        });

        draftState[filterKey].period = period || null;

        switch (true) {
          case ['withinTheNext', 'withinTheLast'].includes(period): {
            const [, frequency, interval] =
              selection.attributes.duration.split(/(\d+)/);

            draftState[filterKey][`${period}Frequency`] = frequency;
            draftState[filterKey][`${period}Interval`] = {
              D: 'days',
              W: 'weeks',
              M: 'months',
              Y: 'years',
            }[interval];
            break;
          }
          case ['before', 'after', 'onExactDate'].includes(period):
            draftState[filterKey][period] = moment(
              selection.attributes.dateOne,
            ).toDate();
            break;
          case period === 'customDateRange':
            draftState[filterKey].customDateRangeFrom = moment(
              selection.attributes.dateOne,
            ).toDate();
            draftState[filterKey].customDateRangeTo = moment(
              selection.attributes.dateTwo,
            ).toDate();
            break;
        }
      });
    })();

    (function seedUserFilterSelections() {
      userFilterSelections.forEach((selection) => {
        const filterKey = `${voca.camelCase(selection.attributes.filterType)}Filter`;

        setSeededFilterSelection({
          filterKey,
          createdAt: selection.attributes.createdAt,
        });

        draftState[filterKey].selectedIds.push(
          selection.relationships.selectedUser.data?.id || null,
        );
      });
    })();

    (function seedFolderFilterSelections() {
      folderFilterSelections.forEach((selection) => {
        const filterKey = 'folderFilter';

        setSeededFilterSelection({
          filterKey,
          createdAt: selection.attributes.createdAt,
        });

        draftState[filterKey].selectedIds.push(
          selection.relationships.selectedFolder.data?.id || null,
        );
      });
    })();

    assignInitialDateFilter({
      draftFilterState: draftState.lastUpdatedFilter,
      customDateRange: 'past',
    });
    assignInitialDateFilter({
      draftFilterState: draftState.reviewDateFilter,
      customDateRange: 'future',
    });
    assignInitialDateFilter({
      draftFilterState: draftState.creationDateFilter,
      customDateRange: 'past',
    });
    assignInitialDateFilter({
      draftFilterState: draftState.startDateFilter,
      customDateRange: 'future',
    });

    draftState.selectedFilters = Array.from(seededFilterSelections)
      .sort((a, b) => a[1] - b[1])
      .map((selection) => selection[0]);
  });

  function setSeededFilterSelection({ createdAt, filterKey }) {
    if (!seededFilterSelections.has(filterKey))
      seededFilterSelections.set(filterKey, moment(createdAt));
  }

  function assignInitialDateFilter({ customDateRange, draftFilterState }) {
    const now = moment();

    if (!draftFilterState.before) {
      draftFilterState.before = now.toDate();
    }

    if (!draftFilterState.after) {
      draftFilterState.after = now.clone().startOf('year').toDate();
    }

    if (!draftFilterState.onExactDate) {
      draftFilterState.onExactDate = now.toDate();
    }

    if (
      !draftFilterState.customDateRangeFrom ||
      !draftFilterState.customDateRangeTo
    ) {
      if (customDateRange === 'past') {
        draftFilterState.customDateRangeFrom = now
          .clone()
          .subtract(1, 'month')
          .toDate();
        draftFilterState.customDateRangeTo = now.toDate();
      } else if (customDateRange === 'future') {
        draftFilterState.customDateRangeFrom = now.toDate();
        draftFilterState.customDateRangeTo = now
          .clone()
          .add(1, 'month')
          .toDate();
      }
    }
  }
}

export function buildBaseProjectFilters({
  dashboardConfigurableFields,
  translations,
}) {
  const filterKeys = [
    ...multiSelectIdsFilterKeys,
    ...dateFilterKeys,
    ...checkboxFilterKeys,
  ];

  const filters = filterKeys.reduce((acc, filterKey) => {
    acc[filterKey] = translations[filterKey.replace('Filter', '')];
    return acc;
  }, {});

  dashboardConfigurableFields.forEach((dashboardConfigurableField) => {
    const filterKey = dashboardConfigurableFieldKey({
      dashboardConfigurableField,
      suffix: 'Filter',
    });

    filters[filterKey] = voca.capitalize(
      dashboardConfigurableField.attributes.label,
    );
  });

  return new Map(
    Object.entries(filters).sort((a, b) =>
      _.lowerCase(a[1]) > _.lowerCase(b[1]) ? 1 : -1,
    ),
  );
}
